import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alerta, Botao, Campo, Cartao } from "@plantus/ui";

import { usarSessao } from "./ProvedorDeSessao";
import { mensagemDeSegundoFator } from "./mensagens";
import { Moldura } from "../layout/Moldura";

interface EstadoDeOrigem {
  de?: string;
}

/**
 * O segundo passo do login.
 *
 * <p>Só é alcançável com uma sessão **parcial** — autenticada por senha e
 * ainda não pelo segundo fator. Chegar aqui de outro jeito devolve ao
 * login: não há nada nesta tela que funcione sem a sessão parcial, porque
 * é ela que o backend exige em `/api/auth/mfa`.
 */
export function PaginaSegundoFator() {
  const { estado, confirmarCodigo } = usarSessao();
  const navegar = useNavigate();
  const local = useLocation();

  const [codigo, definirCodigo] = useState("");
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  const destino = (local.state as EstadoDeOrigem | null)?.de ?? "/";

  if (estado.tipo === "autenticado") {
    return <Navigate to={destino} replace />;
  }
  if (estado.tipo !== "parcial") {
    return <Navigate to="/entrar" replace />;
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    definirErro(null);
    definirEnviando(true);
    try {
      await confirmarCodigo(codigo.trim());
      navegar(destino, { replace: true });
    } catch (falha) {
      definirErro(mensagemDeSegundoFator(falha));
      definirCodigo("");
    } finally {
      definirEnviando(false);
    }
  }

  return (
    <Moldura>
      <Cartao titulo="Confirmação em duas etapas">
        <h1 className="entrada__titulo">Confirmação em duas etapas</h1>
        <p className="entrada__apoio">
          {estado.nome}, informe o código do seu aplicativo autenticador.
        </p>

        <form onSubmit={enviar} noValidate>
          {erro !== null && <Alerta tom="erro">{erro}</Alerta>}

          <div className="entrada__campos">
            <Campo
              rotulo="Código"
              name="codigo"
              // one-time-code faz o celular oferecer o código do SMS ou do
              // gerenciador. Sem isso, a pessoa digita à mão em 30 segundos.
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              required
              value={codigo}
              onChange={(e) => definirCodigo(e.target.value)}
              dica="Seis dígitos, ou um código de recuperação."
            />
          </div>

          <Botao type="submit" largo carregando={enviando}>
            Confirmar
          </Botao>
        </form>
      </Cartao>
    </Moldura>
  );
}
